import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ACTIVE_TAB_PARAM, PUBLIC_PROSPECT_CATEGORY_OPTIONS_WITH_COLORS } from '../../../constants';
import { useAppDispatch, useAppSelector, useGetFiltersFromParams, useUpdateFilterParams } from '../../../hooks';
import { setReviewFilters, updateReviewFilters } from '../../../redux/reducers';
import { useGetTeamsQuery, useGetUsersForSelectQuery, useGetUserTeamsQuery } from '../../../services';
import {
  ComponentSize,
  FilterValues,
  Permissions,
  ProspectCategory,
  ReviewFilterKeys,
  ReviewFilters as ReviewFiltersType,
  ReviewTab,
  Roles,
  TextColor,
} from '../../../types';
import { getTeamsOptions, getUserOptions, isFiltersDirty } from '../../../utils';
import { SelectOption, SidebarDateFilter, SidebarSelectFilter, Toggle, Typography } from '../../shared';
import SidebarFilters from '../SidebarFilters';

interface ReviewFiltersProps {
  isProspectsLoading: boolean;
  isTagsLoading: boolean;
  prospectOptions: SelectOption[];
  tagOptions: SelectOption[];
}

const ReviewFilters = ({ prospectOptions, tagOptions, isProspectsLoading, isTagsLoading }: ReviewFiltersProps) => {
  // Redux
  const dispatch = useAppDispatch();
  const savedFilters = useAppSelector((state) => state.filters.reviewFilters);
  const user = useAppSelector((state) => state.auth.user);
  const isSalesRep = user?.role === Roles.SALES_REP;
  const permissions = user?.permissions || [];
  const canViewHiring = permissions.includes(Permissions.VIEW_CANDIDATE);

  // Custom hooks
  const filters = useGetFiltersFromParams() as ReviewFiltersType;
  const updateFilterParams = useUpdateFilterParams();

  // Queries
  const { data: userData, isLoading: isLoadingUsers } = useGetUsersForSelectQuery({
    includeCurrentUser: true,
    includeDisabledUsers: true,
  });
  const { data: orgTeamsData = [], isLoading: isLoadingOrgTeams } = useGetTeamsQuery();
  const { data: userTeamsData = [] } = useGetUserTeamsQuery(user?.id ?? '');

  const [searchParams] = useSearchParams();

  const currentTabFromParams = searchParams.get(ACTIVE_TAB_PARAM);
  const activeTab = currentTabFromParams as ReviewTab;

  const userOptions = useMemo(() => getUserOptions(userData), [userData]);
  const teamOptions = useMemo(() => getTeamsOptions(orgTeamsData, userTeamsData), [orgTeamsData, userTeamsData]);

  const prospectTypeOptions = useMemo(() => {
    // Filter out candidate options if user doesn't have hiring permissions.
    return PUBLIC_PROSPECT_CATEGORY_OPTIONS_WITH_COLORS.filter(
      (option) => canViewHiring || option.value !== ProspectCategory.INTERVIEW
    );
  }, [canViewHiring]);

  const selectedUsers = [...(filters.incomingPhoneNumber || []), ...(filters.userId || [])];

  // Disable the team and user filters if the other filter has values.
  const isTeamFilterDisabled = !!filters.incomingPhoneNumber || !!filters.userId;
  const isUserFilterDisabled = !!filters.teamIds;

  const updateFilter = useCallback(
    (key: ReviewFilterKeys) => (value?: FilterValues) => {
      dispatch(updateReviewFilters({ [key]: value }));
      updateFilterParams({ [key]: value });
    },
    [dispatch, updateFilterParams]
  );

  const onUserChange = useCallback(
    (newValue?: string[]) => {
      const phoneNumbers = newValue?.filter((value) => value.startsWith('+'));
      const userIds = newValue?.filter((value) => !value.startsWith('+'));
      updateFilter(ReviewFilterKeys.USER)(userIds);
      updateFilter(ReviewFilterKeys.INCOMING_PHONE_NUMBER)(phoneNumbers);
    },
    [updateFilter]
  );

  // This effect checks if there are any filters applied on component mount.
  useEffect(() => {
    const isDirty = isFiltersDirty(filters);
    if (isDirty) {
      // If filters are applied, update the Redux store with the current filters from the URL.
      dispatch(setReviewFilters(filters));
    } else {
      // If no filters are applied, restore the filters from saved settings.
      updateFilterParams(savedFilters);
    }
    // We purposely do not add the filters to the dependency array because we do not want to re-run this effect when the filters change.
  }, [updateFilterParams]);

  return (
    <SidebarFilters>
      <Toggle
        leftLabel={<Typography color={TextColor.SECONDARY}>Flagged calls only</Typography>}
        checked={!!filters.isFlagged}
        onChange={updateFilter(ReviewFilterKeys.FLAGGED)}
        size={ComponentSize.MEDIUM}
      />
      <SidebarDateFilter
        title="Call date"
        onChange={updateFilter(ReviewFilterKeys.CALL_DATE)}
        selected={filters.startTime}
      />
      {/* Only show the Team filter if there are team options */}
      {!!teamOptions.length && (
        <SidebarSelectFilter
          options={teamOptions}
          placeholder="Select teams"
          selected={filters.teamIds}
          title="Team"
          onChange={updateFilter(ReviewFilterKeys.TEAM)}
          loading={isLoadingOrgTeams}
          disabled={isTeamFilterDisabled}
          tooltip={isTeamFilterDisabled ? 'Remove user filter to enable' : undefined}
        />
      )}
      {/* Only show the User filter if the user is not a Sales Representative */}
      {!isSalesRep && (
        <SidebarSelectFilter
          options={userOptions}
          placeholder="Select user"
          selected={selectedUsers}
          title="User"
          onChange={onUserChange}
          loading={isLoadingUsers}
          disabled={isUserFilterDisabled}
          tooltip={isUserFilterDisabled ? 'Remove team filter to enable' : undefined}
        />
      )}
      {activeTab === ReviewTab.SIMULATIONS && (
        <>
          <SidebarSelectFilter
            loading={isProspectsLoading}
            options={prospectOptions}
            placeholder="Select simulation"
            selected={filters.personaId}
            title="Simulation"
            onChange={updateFilter(ReviewFilterKeys.PROSPECT)}
          />
          <SidebarSelectFilter
            loading={isTagsLoading}
            options={tagOptions}
            title="Tags"
            placeholder="Select tag"
            selected={filters.tags}
            onChange={updateFilter(ReviewFilterKeys.TAGS)}
          />
          <SidebarSelectFilter
            title="Category"
            placeholder="Select category"
            selected={filters.categories}
            onChange={updateFilter(ReviewFilterKeys.CATEGORY)}
            options={prospectTypeOptions}
          />
        </>
      )}
    </SidebarFilters>
  );
};

export default ReviewFilters;
